import React, { useState } from 'react';
import API from '../services/api';
import { Link } from 'react-router-dom';
import Icon from '../components/ui/Icon';
import { getToken, getUser } from '../utils/authUtil';
import './Landing.css';

const FEATURES = [
  { icon: 'test', title: 'Timed aptitude assessments', body: 'Placement officers assign quantitative, logical and verbal tests with negative marking, sections and a fair timer.' },
  { icon: 'readiness', title: 'A clear readiness score', body: 'Every attempt feeds one Placement Readiness score with an honest confidence level, not a guess.' },
  { icon: 'mistake', title: 'Mistake bank and practice', body: 'Students revisit what they got wrong and practise topic by topic, at their own pace.' },
  { icon: 'chart', title: 'Analytics for the placement cell', body: 'Batch, department and student views show who is ready, who needs attention and where to intervene.' },
  { icon: 'achievement', title: 'Progress that motivates', body: 'Levels, streaks, achievements and daily missions keep students practising consistently.' },
  { icon: 'shield', title: 'Each college on its own', body: 'Every institution’s data is kept separate. Colleges manage their own students, batches and announcements.' },
];

const ROLES = [
  { icon: 'profile', title: 'For students', body: 'Know where you stand, what to practise next, and how close you are to placement-ready.' },
  { icon: 'users', title: 'For placement officers', body: 'Run assessments, target announcements to a department or batch, and act on real data.' },
  { icon: 'building', title: 'For institutions', body: 'A consistent, measurable approach to placement preparation across every batch.' },
];

function LeadForm() {
  const [f, setF] = useState({ name: '', email: '', organization: '', message: '', website: '' });
  const [state, setState] = useState({ busy: false, done: false, error: '' });
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, error: '' });
    try { await API.post('/leads', f); setState({ busy: false, done: true, error: '' }); }
    catch (err) { setState({ busy: false, done: false, error: err.response?.data?.message || (err.response ? 'Could not send your request.' : 'You appear to be offline. Please try again.') }); }
  };
  if (state.done) return <p className="lp-lead-done" role="status">Thanks &mdash; we&apos;ll be in touch soon.</p>;
  return (
    <form className="lp-form" onSubmit={submit}>
      <label>Your name<input value={f.name} onChange={set('name')} required maxLength={120} autoComplete="name" /></label>
      <label>Work email<input type="email" value={f.email} onChange={set('email')} required maxLength={254} autoComplete="email" /></label>
      <label>College / organisation<input value={f.organization} onChange={set('organization')} maxLength={160} autoComplete="organization" /></label>
      <label>How can we help? (optional)<textarea value={f.message} onChange={set('message')} rows={3} maxLength={2000} /></label>
      <input className="lp-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set('website')} name="website" />
      {state.error && <p className="lp-form-error" role="alert">{state.error}</p>}
      <button className="lp-btn lp-btn--primary lp-btn--lg" type="submit" disabled={state.busy}>{state.busy ? 'Sending…' : 'Request a demo'}</button>
    </form>
  );
}

/** Public landing page. No sign-up or social login is offered because accounts are created by a college administrator. */
export default function Landing() {
  const user = getToken() ? getUser() : null;
  const home = user?.realRole === 'super_admin' || user?.role === 'super_admin' ? '/super-admin' : user?.role === 'student' ? '/student/dashboard' : '/admin/dashboard';

  return (
    <div className="lp">
      <header className="lp-nav">
        <Link to="/" className="lp-brand" aria-label="Zaptude home">
          <img src="/branding/zaptude-mark.svg" alt="" width="32" height="32" /><span>Zaptude</span>
        </Link>
        <nav className="lp-nav-actions" aria-label="Account">
          {user ? <Link className="lp-btn lp-btn--primary" to={home}>Go to dashboard</Link> : <Link className="lp-btn lp-btn--primary" to="/login">Sign in</Link>}
        </nav>
      </header>

      <main>
        <section className="lp-hero">
          <p className="lp-eyebrow">Aptitude &amp; placement readiness</p>
          <h1>Know exactly how ready every student is for placements.</h1>
          <p className="lp-lead">Zaptude helps colleges assess aptitude, spot skill gaps early and guide students with a clear readiness score, so placement preparation is measured, not guessed.</p>
          <div className="lp-cta">
            <Link className="lp-btn lp-btn--primary lp-btn--lg" to="/login">Sign in</Link>
            <a className="lp-btn lp-btn--ghost lp-btn--lg" href="#lp-contact">Get Zaptude for your college</a>
          </div>
          <p className="lp-note">Student and administrator accounts are created by your college.</p>
        </section>

        <section className="lp-section" aria-labelledby="lp-problem">
          <h2 id="lp-problem">The problem</h2>
          <p className="lp-prose">Placement preparation often runs on scattered mock tests and spreadsheets. Students don&apos;t know where they are weak, and placement cells find out too late who needed help. Zaptude puts assessments, practice and analytics in one place.</p>
        </section>

        <section className="lp-section" aria-labelledby="lp-features">
          <h2 id="lp-features">What you get</h2>
          <div className="lp-grid">
            {FEATURES.map(f => (
              <article className="lp-card" key={f.title}>
                <span className="lp-card-icon"><Icon name={f.icon} size={22} /></span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-section" aria-labelledby="lp-who">
          <h2 id="lp-who">Who it&apos;s for</h2>
          <div className="lp-grid lp-grid--3">
            {ROLES.map(r => (
              <article className="lp-card" key={r.title}>
                <span className="lp-card-icon"><Icon name={r.icon} size={22} /></span>
                <h3>{r.title}</h3>
                <p>{r.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-section lp-contact" id="lp-contact" aria-labelledby="lp-contact-h">
          <h2 id="lp-contact-h">Bring Zaptude to your college</h2>
          <p className="lp-prose">Tell us a little about your institution and we&apos;ll get back to you.</p>
          <LeadForm />
        </section>

        <section className="lp-band">
          <h2>Ready to sign in?</h2>
          <Link className="lp-btn lp-btn--light lp-btn--lg" to="/login">Sign in to Zaptude</Link>
        </section>
      </main>

      <footer className="lp-footer">
        <span>&copy; {new Date().getFullYear()} Zaptude</span>
        <span>Developed by <a href="https://levrotec.com" target="_blank" rel="noopener noreferrer">Levrotec</a> &mdash; SaaS development company, Chennai</span>
      </footer>
    </div>
  );
}
